'use client';

import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import CssBaseline from '@mui/material/CssBaseline';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { useServerInsertedHTML } from 'next/navigation';
import React, { useState } from 'react';

// Create MUI theme with comprehensive color palette
const theme = createTheme({
  palette: {
    primary: {
      main: '#220000',
      light: '#4a4a4a',
      dark: '#000000',
    },
    secondary: {
      main: '#EB722E', // Orange color used in banner
      light: '#F5A623',
      dark: '#D65A1A',
    },
    background: {
      default: '#ffffff',
      paper: '#fafafa',
    },
    text: {
      primary: '#220000',
      secondary: '#666666',
    },
  },
  typography: {
    fontFamily: 'var(--font-inter), Inter, sans-serif',
    h1: {
      fontFamily: 'var(--font-inter), Inter, sans-serif',
      fontWeight: 400,
      lineHeight: 1.21,
    },
    h2: {
      fontFamily: 'var(--font-inter), Inter, sans-serif',
      fontWeight: 700,
      lineHeight: 1.21,
    },
    body1: {
      fontFamily: 'var(--font-inter), Inter, sans-serif',
      lineHeight: 1.5,
    },
    body2: {
      fontFamily: 'var(--font-inter), Inter, sans-serif',
      lineHeight: 1.5,
    },
    button: {
      fontFamily: 'var(--font-sarabun), Sarabun, sans-serif',
      textTransform: 'uppercase',
      letterSpacing: 'wide',
      fontWeight: 400,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '5px',
          textTransform: 'uppercase',
          letterSpacing: 'wide',
          fontFamily: 'var(--font-sarabun), Sarabun, sans-serif',
          fontWeight: 400,
        },
        contained: {
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'scale(1.1)',
          },
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          fontFamily: 'var(--font-inter), Inter, sans-serif',
        },
      },
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1920,
    },
  },
});

interface MUIThemeProviderProps {
  children: React.ReactNode;
}

export default function MUIThemeProvider({ children }: MUIThemeProviderProps) {
  const [{ cache, flush }] = useState(() => {
    const cache = createCache({
      key: 'mui',
      prepend: true, // This ensures MUI styles are loaded first
    });
    cache.compat = true;
    const prevInsert = cache.insert;
    let inserted: string[] = [];
    cache.insert = (...args) => {
      const serialized = args[1];
      if (cache.inserted[serialized.name] === undefined) {
        inserted.push(serialized.name);
      }
      return prevInsert(...args);
    };
    const flush = () => {
      const prevInserted = inserted;
      inserted = [];
      return prevInserted;
    };
    return { cache, flush };
  });

  useServerInsertedHTML(() => {
    const names = flush();
    if (names.length === 0) {
      return null;
    }
    let styles = '';
    for (const name of names) {
      styles += cache.inserted[name];
    }
    return (
      <style
        key={cache.key}
        data-emotion={`${cache.key} ${names.join(' ')}`}
        dangerouslySetInnerHTML={{
          __html: styles,
        }}
      />
    );
  });

  return (
    <CacheProvider value={cache}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </CacheProvider>
  );
}
